import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Button, 
  Typography, 
  Space, 
  Tag, 
  Row, 
  Col, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Switch, 
  message,
  Table,
  Tooltip,
  Divider,
  Alert
} from 'antd';
import { 
  RobotOutlined, 
  PlusOutlined, 
  EditOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  SettingOutlined,
  ClockCircleOutlined,
  UserOutlined
} from '@ant-design/icons';
import { emailAutomationService } from '../../../services/emailMarketingService';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const EmailAutomation = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchAutomations();
  }, []);

  const fetchAutomations = async () => {
    try {
      setLoading(true);
      const automationsData = await emailAutomationService.getAutomationRules();
      setAutomations(automationsData);
    } catch (error) {
      console.error('Error fetching automations:', error);
      message.error('Failed to load automations');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAutomation = async (values) => {
    try {
      const automationData = {
        name: values.name,
        trigger: values.trigger,
        conditions: values.conditions,
        emailTemplate: values.emailTemplate,
        delay: values.delay,
        isActive: values.isActive || true,
        description: values.description
      };

      await emailAutomationService.createAutomationRule(automationData);
      message.success('Automation rule created successfully');
      setModalVisible(false);
      form.resetFields();
      fetchAutomations();
    } catch (error) {
      console.error('Error creating automation:', error);
      message.error('Failed to create automation rule');
    }
  };

  const handleToggleAutomation = async (automationId, isActive) => {
    try {
      await emailAutomationService.updateAutomation(automationId, { isActive });
      message.success(`Automation ${isActive ? 'activated' : 'paused'}`);
      fetchAutomations();
    } catch (error) {
      console.error('Error toggling automation:', error);
      message.error('Failed to update automation');
    }
  };

  const getTriggerIcon = (trigger) => {
    const icons = {
      'signup': <UserOutlined />,
      'service-completed': <ClockCircleOutlined />,
      'workshop-registered': <SettingOutlined />,
      'payment-received': <ClockCircleOutlined />,
      'inactive-customer': <UserOutlined />
    };
    return icons[trigger] || <RobotOutlined />;
  };

  const columns = [
    {
      title: 'Automation Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong>{text}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '0.8rem' }}>
            {record.description}
          </Text>
        </div>
      )
    },
    {
      title: 'Trigger',
      dataIndex: 'trigger',
      key: 'trigger',
      render: (trigger) => (
        <Space>
          {getTriggerIcon(trigger)}
          <Tag color="blue">{trigger.replace('-', ' ').toUpperCase()}</Tag>
        </Space>
      )
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={(checked) => handleToggleAutomation(record.id, checked)}
          checkedChildren="Active"
          unCheckedChildren="Paused"
        />
      )
    },
    {
      title: 'Triggers',
      dataIndex: 'triggerCount',
      key: 'triggerCount',
      render: (count) => (
        <Text strong style={{ color: '#1890ff' }}>
          {count || 0}
        </Text>
      )
    },
    {
      title: 'Last Triggered',
      dataIndex: 'lastTriggered',
      key: 'lastTriggered',
      render: (date) => date ? new Date(date).toLocaleDateString() : 'Never'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEditAutomation(record)}
            />
          </Tooltip>
          <Tooltip title={record.isActive ? "Pause" : "Activate"}>
            <Button 
              type="text" 
              icon={record.isActive ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              size="small"
              onClick={() => handleToggleAutomation(record.id, !record.isActive)}
            />
          </Tooltip>
        </Space>
      )
    }
  ];

  const handleEditAutomation = (automation) => {
    setEditingAutomation(automation);
    form.setFieldsValue(automation);
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setEditingAutomation(null);
    form.resetFields();
  };

  const automationTemplates = [
    {
      name: "Welcome Series",
      trigger: "signup",
      description: "Send welcome emails to new customers",
      template: "welcome-series"
    },
    {
      name: "Service Follow-up",
      trigger: "service-completed",
      description: "Request reviews after service completion",
      template: "service-followup"
    },
    {
      name: "Workshop Reminder",
      trigger: "workshop-registered",
      description: "Remind customers about upcoming workshops",
      template: "workshop-reminder"
    },
    {
      name: "Inactive Customer",
      trigger: "inactive-customer",
      description: "Re-engage customers who haven't used services recently",
      template: "inactive-customer"
    }
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              Email Automation
            </Title>
            <Text type="secondary">
              Set up automated email sequences based on customer actions
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => setModalVisible(true)}
          >
            Create Automation
          </Button>
        </div>

        <Alert
          message="Automation Templates"
          description="Quick-start templates for common email automation scenarios"
          type="info"
          showIcon
          style={{ marginBottom: '1.5rem' }}
        />

        {/* Automation Templates */}
        <Row gutter={[16, 16]} style={{ marginBottom: '1.5rem' }}>
          {automationTemplates.map((template, index) => (
            <Col xs={24} sm={12} lg={6} key={index}>
              <Card
                size="small"
                hoverable
                onClick={() => {
                  form.setFieldsValue(template);
                  setModalVisible(true);
                }}
                style={{ cursor: 'pointer' }}
              >
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    {getTriggerIcon(template.trigger)}
                  </div>
                  <Title level={5} style={{ margin: 0, fontSize: '1rem' }}>
                    {template.name}
                  </Title>
                  <Text type="secondary" style={{ fontSize: '0.8rem' }}>
                    {template.description}
                  </Text>
                </div>
              </Card>
            </Col>
          ))}
        </Row>

        <Table
          columns={columns}
          dataSource={automations}
          loading={loading}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      {/* Create/Edit Automation Modal */}
      <Modal
        title={editingAutomation ? "Edit Automation" : "Create New Automation"}
        open={modalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateAutomation}
        >
          <Form.Item
            name="name"
            label="Automation Name"
            rules={[{ required: true, message: 'Please enter automation name' }]}
          >
            <Input placeholder="Enter automation name" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: true, message: 'Please enter description' }]}
          >
            <TextArea
              rows={2}
              placeholder="Describe what this automation does"
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="trigger"
                label="Trigger Event"
                rules={[{ required: true, message: 'Please select trigger event' }]}
              >
                <Select placeholder="Select trigger event">
                  <Option value="signup">Customer Signup</Option>
                  <Option value="service-completed">Service Completed</Option>
                  <Option value="workshop-registered">Workshop Registered</Option>
                  <Option value="payment-received">Payment Received</Option>
                  <Option value="inactive-customer">Inactive Customer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="delay"
                label="Delay (hours)"
                rules={[{ required: true, message: 'Please enter delay' }]}
              >
                <Input
                  type="number"
                  placeholder="24"
                  addonAfter="hours"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="emailTemplate"
            label="Email Template"
            rules={[{ required: true, message: 'Please select email template' }]}
          >
            <Select placeholder="Select email template">
              <Option value="welcome-series">Welcome Series</Option>
              <Option value="service-followup">Service Follow-up</Option>
              <Option value="workshop-reminder">Workshop Reminder</Option>
              <Option value="seasonal-tips">Seasonal Tips</Option>
              <Option value="inactive-customer">Inactive Customer</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="conditions"
            label="Conditions (Optional)"
          >
            <TextArea
              rows={3}
              placeholder="Additional conditions for triggering this automation"
            />
          </Form.Item>

          <Form.Item
            name="isActive"
            label="Status"
            valuePropName="checked"
          >
            <Switch
              checkedChildren="Active"
              unCheckedChildren="Paused"
              defaultChecked
            />
          </Form.Item>

          <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
            <Space>
              <Button onClick={handleModalClose}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                {editingAutomation ? 'Update Automation' : 'Create Automation'}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailAutomation;
